import React, { useState, useEffect, useCallback, useMemo } from 'react';
import InformeCRMButton from './InformeCRMButton';
import { C } from '../ui/theme.js';

// ==================== SUPABASE CONFIG ====================
const SUPABASE_URL = 'https://zkznupgyaorojxpydlly.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inprem51cGd5YW9yb2p4cHlkbGx5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjYwMjA5NTMsImV4cCI6MjA4MTU5Njk1M30.gKCpGxBeOCaaCRm10qQ_YpxXwTZQGbtTREsWrUG2RFY';

const REFRESH_MS = 5 * 60 * 1000; // 5 minutos

const fetchDashboardStats = async (fromISO, toISO) => {
  try {
    const body = {};
    if (fromISO) body.p_from = fromISO;
    if (toISO)   body.p_to   = toISO;
    const response = await fetch(`${SUPABASE_URL}/rest/v1/rpc/get_dashboard_stats`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error('RPC ' + response.status);
    return await response.json();
  } catch (e) {
    console.error('Dashboard stats RPC error:', e);
    return null;
  }
};

// Helpers de fecha (ISO date "YYYY-MM-DD" → timestamptz string)
const monthStartISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const toRpcFrom = (isoDate) => isoDate ? `${isoDate}T00:00:00+02:00` : null;
const toRpcTo   = (isoDate) => isoDate ? `${isoDate}T23:59:59+02:00` : null;
const fmtDateLabel = (isoDate) => {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-');
  return `${d}/${m}/${y.slice(2)}`;
};

// ==================== PALETA ====================
// La paleta del Resumen ya no es propia: son los tokens de la suite
// (`src/ui/theme.js`). Se mantiene el objeto PAL con los mismos nombres para no
// tocar las 900 referencias que hay repartidas por el fichero.
const PAL = {
  bg: C.bg,
  card: C.card,
  border: C.line,
  primary: C.brand,
  primaryDark: C.brandDark,
  accent: '#1a9cd4',
  light: '#7cc4e8',
  success: C.verde,
  warning: C.naranja,
  danger: C.rojo,
  text: C.ink,
  textMuted: C.muted,
  textSubtle: C.faint,
};

const CHART_PALETTE = [
  C.brand, '#1a9cd4', '#7cc4e8', C.verde, C.naranja,
  C.rojo, C.morado, '#d96ba8', '#2aa39b', '#5b6bd0',
];

// ==================== HELPERS ====================
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
};

const fmt = (n) => (n == null ? '0' : Number(n).toLocaleString('es-ES'));

// ==================== CHARTS SVG ====================

// Donut: data = [{label, value, color}]
const DonutChart = ({ data = [], size = 180, thickness = 28, centerLabel = '', centerValue = '' }) => {
  const total = data.reduce((s, d) => s + (d.value || 0), 0);
  const radius = (size - thickness) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * radius;

  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={radius} fill="none" stroke={PAL.border} strokeWidth={thickness} />
      {total > 0 && data.map((d, i) => {
        const value = d.value || 0;
        if (value === 0) return null;
        const length = (value / total) * circumference;
        const dash = `${length} ${circumference - length}`;
        const dashOffset = circumference - offset;
        offset += length;
        return (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke={d.color}
            strokeWidth={thickness}
            strokeDasharray={dash}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${cx} ${cy})`}
            style={{ transition: 'all 0.4s ease' }}
          />
        );
      })}
      <text x={cx} y={cy - 4} textAnchor="middle" fontSize="26" fontWeight="700" fill={PAL.text}>
        {centerValue !== '' ? centerValue : total}
      </text>
      <text x={cx} y={cy + 18} textAnchor="middle" fontSize="11" fontWeight="600" fill={PAL.textMuted} style={{ textTransform: 'uppercase', letterSpacing: '0.4px' }}>
        {centerLabel}
      </text>
    </svg>
  );
};

// Leyenda compacta para donut/pie
const Legend = ({ data = [], compact = false }) => {
  const total = data.reduce((s, d) => s + (d.value || 0), 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? '4px' : '6px' }}>
      {data.map((d, i) => {
        const pct = total > 0 ? Math.round((d.value / total) * 100) : 0;
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
            <span style={{ width: '10px', height: '10px', borderRadius: '3px', background: d.color, flexShrink: 0 }} />
            <span style={{ color: PAL.textMuted, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label}</span>
            <span style={{ color: PAL.text, fontWeight: 700 }}>{fmt(d.value)}</span>
            <span style={{ color: PAL.textSubtle, fontWeight: 500, minWidth: '34px', textAlign: 'right' }}>{pct}%</span>
          </div>
        );
      })}
    </div>
  );
};

// Barras horizontales: data = [{label, value, color}]
const HorizontalBars = ({ data = [], height = 22 }) => {
  const max = Math.max(1, ...data.map(d => d.value || 0));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {data.map((d, i) => {
        const pct = max > 0 ? (d.value / max) * 100 : 0;
        return (
          <div key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '2px' }}>
              <span style={{ fontSize: '12px', color: PAL.textMuted, fontWeight: 600 }}>{d.label}</span>
              <span style={{ fontSize: '13px', color: PAL.text, fontWeight: 700 }}>{fmt(d.value)}</span>
            </div>
            <div style={{ height: `${height}px`, background: PAL.border, borderRadius: '6px', overflow: 'hidden' }}>
              <div style={{
                width: `${pct}%`,
                height: '100%',
                background: `linear-gradient(90deg, ${d.color}, ${d.colorTo || d.color})`,
                borderRadius: '6px',
                transition: 'width 0.5s ease',
              }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ==================== TARJETAS ====================

const KPICard = ({ label, value, sub, color = PAL.primary, onClick, sparkValue }) => (
  <div
    onClick={onClick}
    style={{
      background: PAL.card,
      borderRadius: '14px',
      padding: '16px 18px',
      border: `1px solid ${PAL.border}`,
      boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.15s ease, box-shadow 0.15s ease',
      position: 'relative',
      overflow: 'hidden',
    }}
    onMouseEnter={(e) => { if (onClick) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(15,23,42,0.10)'; } }}
    onMouseLeave={(e) => { if (onClick) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(15,23,42,0.04)'; } }}
  >
    <div style={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', background: color }} />
    <div style={{ fontSize: '11px', color: PAL.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>{label}</div>
    <div style={{ fontSize: '28px', fontWeight: 800, color: PAL.text, lineHeight: 1, marginBottom: '4px' }}>{value}</div>
    {sub && <div style={{ fontSize: '12px', color: PAL.textMuted }}>{sub}</div>}
  </div>
);

const ChartCard = ({ title, icon, children, onClick, action, active = false }) => (
  <div
    style={{
      background: PAL.card,
      borderRadius: '14px',
      padding: '16px 18px',
      border: `1px solid ${active ? PAL.primary : PAL.border}`,
      boxShadow: active ? `0 4px 14px rgba(0,81,135,0.18)` : '0 1px 3px rgba(15,23,42,0.04)',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
      display: 'flex',
      flexDirection: 'column',
    }}
    onClick={onClick}
    onMouseEnter={(e) => { if (onClick && !active) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(15,23,42,0.10)'; } }}
    onMouseLeave={(e) => { if (onClick && !active) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(15,23,42,0.04)'; } }}
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {icon && <span style={{ fontSize: '16px' }}>{icon}</span>}
        <h3 style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: PAL.text, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{title}</h3>
      </div>
      {action && <span style={{ fontSize: '11px', color: active ? PAL.danger : PAL.primary, fontWeight: 700, background: active ? '#fcf1f0' : 'transparent', padding: active ? '2px 8px' : 0, borderRadius: '8px' }}>{action} {active ? '✕' : '→'}</span>}
    </div>
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>{children}</div>
  </div>
);

// Spinner ligero para overlay de "Cargando..."
const Spinner = () => (
  <div style={{ display: 'inline-block', width: '14px', height: '14px', border: `2px solid ${PAL.border}`, borderTopColor: PAL.primary, borderRadius: '50%', animation: 'dimoSpin 0.8s linear infinite' }} />
);

// ==================== PANEL DE SALUD ====================
// Semáforo de todo lo que puede romperse y dejarnos sin leads: el buzón de
// Outlook, cada portal por separado, los flujos de n8n, el backend de
// Customer360, la asignación de AutoLeads y las contraseñas del portal
// Stellantis (que caducan cada 3-6 meses).
//
// Toda la lógica y las credenciales viven en la Edge Function `salud`; aquí
// solo se pinta lo que devuelve y se disparan las acciones correctoras.

const SALUD_URL = `${SUPABASE_URL}/functions/v1/salud`;
const SALUD_REFRESH_MS = 2 * 60 * 1000;

// Solo dos estados: o va bien o falla. El ámbar le quitaba fuerza al rojo.
const SALUD_COLOR = {
  ok: PAL.success,
  aviso: PAL.danger,
  fallo: PAL.danger,
  desconocido: PAL.danger,
};

// Acciones que cortan algo en marcha: piden un segundo clic de confirmación.
const SALUD_ACCION_DELICADA = new Set(['reiniciar_backend', 'reiniciar_n8n']);

// El color solo no basta: cada ficha lleva también la palabra.
const SALUD_PALABRA = { ok: 'Bien', aviso: 'Falla', fallo: 'Falla', desconocido: 'Falla' };

const Semaforo = ({ estado, size = 10, pulso = false }) => (
  <span
    style={{
      display: 'inline-block',
      width: `${size}px`,
      height: `${size}px`,
      borderRadius: '50%',
      background: SALUD_COLOR[estado] || PAL.textSubtle,
      boxShadow: estado === 'fallo' ? `0 0 0 3px ${PAL.danger}22` : 'none',
      animation: pulso && estado === 'fallo' ? 'dimoPulse 1.6s ease-in-out infinite' : 'none',
      flexShrink: 0,
    }}
  />
);

const etiquetaCorta = (t = '') => t.replace(/\s*\(.*?\)\s*/g, '').trim();

const PanelSalud = ({ compacto = false }) => {
  const [salud, setSalud] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState(null);
  const [abierto, setAbierto] = useState(false);
  const [seleccion, setSeleccion] = useState(null);
  const [enCurso, setEnCurso] = useState(null);
  const [confirmando, setConfirmando] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [autoAbierto, setAutoAbierto] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await fetch(SALUD_URL, {
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
      });
      const j = await r.json();
      if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
      setSalud(j);
      setFallo(null);
    } catch (e) {
      setFallo(e.message || 'No se pudo consultar el estado');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargar();
    const t = setInterval(cargar, SALUD_REFRESH_MS);
    return () => clearInterval(t);
  }, [cargar]);

  // La primera vez que hay un fallo se abre solo. Después manda el usuario.
  useEffect(() => {
    if (!autoAbierto && salud?.resumen?.estado === 'fallo') { setAbierto(true); setAutoAbierto(true); }
  }, [salud, autoAbierto]);

  const ejecutar = async (accion, parametro) => {
    const id = `${accion}:${parametro || ''}`;
    if (SALUD_ACCION_DELICADA.has(accion) && confirmando !== id) {
      setConfirmando(id);
      setTimeout(() => setConfirmando((c) => (c === id ? null : c)), 6000);
      return;
    }
    setConfirmando(null);
    setEnCurso(id);
    setResultado(null);
    try {
      const r = await fetch(SALUD_URL, {
        method: 'POST',
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ accion, parametro: parametro || null }),
      });
      const j = await r.json();
      setResultado({ ok: !!j.ok, texto: j.detalle || j.error || (j.ok ? 'Hecho' : 'No se pudo completar') });
    } catch (e) {
      setResultado({ ok: false, texto: e.message || 'Error de red' });
    } finally {
      setEnCurso(null);
      setTimeout(cargar, 2500);
    }
  };

  const checks = salud?.checks || [];
  const resumen = salud?.resumen;
  const canales = checks.filter((c) => c.grupo === 'canal');
  const sistema = checks.filter((c) => c.grupo !== 'canal');
  const estadoGlobal = fallo ? 'desconocido' : resumen?.estado || 'desconocido';
  const detalle = seleccion ? checks.find((c) => c.clave === seleccion) : null;

  const parrafo = fallo
    ? 'No se ha podido consultar el estado del sistema. Puede ser un corte momentáneo; se reintenta solo cada dos minutos.'
    : resumen?.texto || 'Comprobando cómo va todo…';

  const Ficha = ({ c }) => {
    const activo = seleccion === c.clave;
    const mal = c.estado !== 'ok';
    return (
      <div
        onClick={() => { setSeleccion(activo ? null : c.clave); setResultado(null); }}
        title={c.queSignifica || ''}
        style={{
          border: `1px solid ${mal ? PAL.danger : activo ? PAL.primary : PAL.border}`,
          borderRadius: '9px',
          padding: '7px 9px',
          cursor: 'pointer',
          // El fallo tiñe la tarjeta entera: se tiene que ver desde la puerta.
          background: mal ? PAL.danger : activo ? '#f8fafc' : 'white',
          boxShadow: mal ? `0 2px 8px ${PAL.danger}40` : 'none',
          minWidth: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '1px' }}>
          {!mal && <Semaforo estado={c.estado} size={7} />}
          <span style={{ fontSize: '11.5px', fontWeight: 800, color: mal ? 'white' : PAL.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {c.etiqueta}
          </span>
          {!mal && (
            <span style={{ marginLeft: 'auto', fontSize: '9.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: PAL.success }}>
              Bien
            </span>
          )}
        </div>
        <div style={{ fontSize: '11px', color: mal ? 'rgba(255,255,255,0.95)' : PAL.textMuted, lineHeight: 1.35 }}>{c.titular}</div>
        {(c.causas || []).map((causa, i) => (
          <div key={i} style={{ fontSize: '10.5px', color: mal ? 'white' : PAL.textMuted, marginTop: '3px', lineHeight: 1.3, fontWeight: mal ? 600 : 400 }}>
            {causa}
          </div>
        ))}
      </div>
    );
  };

  const Grupo = ({ titulo, subtitulo, items }) => {
    if (!items.length) return null;
    return (
      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '11px', fontWeight: 800, color: PAL.textSubtle, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '2px' }}>{titulo}</div>
        <div style={{ fontSize: '11.5px', color: PAL.textMuted, marginBottom: '7px' }}>{subtitulo}</div>
        <div style={{ display: 'grid', gridTemplateColumns: compacto ? 'repeat(2, minmax(0, 1fr))' : `repeat(${Math.min(6, Math.max(4, Math.ceil(items.length / 2)))}, minmax(0, 1fr))`, gap: '7px' }}>
          {items.map((c) => <Ficha key={c.clave} c={c} />)}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        background: PAL.card,
        borderRadius: '14px',
        border: `1px solid ${estadoGlobal === 'fallo' ? PAL.danger : PAL.border}`,
        boxShadow: estadoGlobal === 'fallo' ? `0 2px 12px ${PAL.danger}22` : '0 1px 3px rgba(15,23,42,0.04)',
        overflow: 'hidden',
      }}
    >
      {/* CABECERA — el párrafo se lee sin tener que abrir nada */}
      <div
        onClick={() => setAbierto((a) => !a)}
        style={{ display: 'flex', alignItems: 'flex-start', gap: '11px', padding: compacto ? '11px 12px' : '13px 16px', cursor: 'pointer' }}
      >
        <div style={{ paddingTop: '3px' }}><Semaforo estado={estadoGlobal} size={compacto ? 11 : 13} pulso /></div>

        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: compacto ? '13px' : '14.5px', fontWeight: 800, color: estadoGlobal === 'fallo' ? PAL.danger : PAL.text }}>
              {fallo ? 'No se ha podido comprobar' : resumen?.titular || 'Comprobando…'}
            </span>
            {resumen && (
              <span style={{ fontSize: '11px', color: PAL.textSubtle }}>
                {resumen.leads24} {resumen.leads24 === 1 ? 'lead' : 'leads'} en 24 h
              </span>
            )}
          </div>
          <div style={{ fontSize: compacto ? '12px' : '12.5px', color: PAL.textMuted, lineHeight: 1.5, marginTop: '3px' }}>
            {parrafo}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          <button
            onClick={(e) => { e.stopPropagation(); cargar(); }}
            title="Volver a comprobar ahora"
            style={{ border: `1px solid ${PAL.border}`, background: 'white', borderRadius: '8px', padding: '4px 8px', fontSize: '11px', color: PAL.textMuted, cursor: 'pointer' }}
          >
            {cargando ? <Spinner /> : '↻'}
          </button>
          <span style={{ color: PAL.textSubtle, fontSize: '12px' }}>{abierto ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* DETALLE */}
      {abierto && (
        <div style={{ padding: compacto ? '0 12px 12px' : '0 16px 14px', borderTop: `1px solid ${PAL.border}`, paddingTop: '12px', animation: 'dimoDetailExpand 0.2s ease' }}>
          <Grupo
            titulo="Por dónde entran los leads"
            subtitulo="Una tarjeta por portal. Si algo de la maquinaria falla, se avisa en los portales a los que afecta."
            items={canales}
          />
          <Grupo
            titulo="Otros avisos"
            subtitulo="Cosas que no dependen de un portal concreto"
            items={sistema}
          />

          {detalle && (
            <div style={{ marginTop: '4px', border: `1px solid ${PAL.border}`, borderRadius: '10px', padding: '11px 13px', background: '#fbfcfd' }}>
              <div style={{ fontSize: '13px', fontWeight: 800, color: PAL.text, marginBottom: '5px' }}>{detalle.etiqueta}</div>
              {detalle.queSignifica && (
                <div style={{ fontSize: '12.5px', color: PAL.textMuted, lineHeight: 1.5, marginBottom: '7px' }}>
                  <strong style={{ color: PAL.text }}>Qué es: </strong>{detalle.queSignifica}
                </div>
              )}
              {detalle.estado !== 'ok' && detalle.queHacer && (
                <div style={{ fontSize: '12.5px', color: PAL.textMuted, lineHeight: 1.5, marginBottom: '9px' }}>
                  <strong style={{ color: PAL.text }}>Qué hacer: </strong>{detalle.queHacer}
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                {(detalle.acciones || []).length === 0 && (
                  <span style={{ fontSize: '11.5px', color: PAL.textSubtle }}>Aquí no hay nada que pulsar: esto se revisa a mano.</span>
                )}
                {(detalle.acciones || []).map((a) => {
                  const id = `${a.clave}:${a.parametro || ''}`;
                  const pidiendo = confirmando === id;
                  const trabajando = enCurso === id;
                  return (
                    <button
                      key={id}
                      disabled={trabajando}
                      onClick={(e) => { e.stopPropagation(); ejecutar(a.clave, a.parametro); }}
                      style={{
                        border: `1px solid ${pidiendo ? PAL.danger : PAL.primary}`,
                        background: pidiendo ? PAL.danger : PAL.primary,
                        color: 'white', borderRadius: '8px', padding: '7px 13px',
                        fontSize: '11.5px', fontWeight: 700,
                        cursor: trabajando ? 'wait' : 'pointer', opacity: trabajando ? 0.6 : 1,
                      }}
                    >
                      {trabajando ? 'Un momento…' : pidiendo ? '¿Seguro? Pulsa otra vez' : a.etiqueta}
                    </button>
                  );
                })}
              </div>
              {resultado && (
                <div style={{ marginTop: '9px', fontSize: '12px', color: resultado.ok ? PAL.success : PAL.danger, fontWeight: 600 }}>
                  {resultado.ok ? '✓ ' : '✕ '}{resultado.texto}
                </div>
              )}
            </div>
          )}

          {salud?.generado && (
            <div style={{ marginTop: '10px', fontSize: '10.5px', color: PAL.textSubtle }}>
              Comprobado a las {new Date(salud.generado).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
              {salud.noche ? ' · de noche se es menos exigente con los portales' : ''} · se revisa solo cada 2 minutos
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== DATOS PARA CHARTS ====================

const buildChannelData = (data) => ([
  { label: 'Email Leads',    value: data.emailLeads?.mes    || 0, color: '#005187' },
  { label: 'Customer Leads', value: data.customerLeads?.mes || 0, color: '#1a9cd4' },
  { label: 'Meta Leads',     value: data.metaLeads?.mes     || 0, color: '#7cc4e8' },
]);

const buildOriginData = (data) => {
  const e = data.emailLeads?.origenes || {};
  const m = data.metaLeads?.origenes || {};
  const c = data.customerLeads?.origenes || {};
  const customerTotal =
    (c.peugeot || 0) + (c.citroen || 0) + (c.opel || 0) + (c.jeep || 0) +
    (c.fiat || 0) + (c.fiatpro || 0) + (c.abarth || 0) + (c.leapmotor || 0);

  const items = [
    { label: 'Carnovo',        value: e.carnovo     || 0 },
    { label: 'Coches.net VN',  value: e.cochesnetVN || 0 },
    { label: 'Coches.net VO',  value: e.cochesnetVO || 0 },
    { label: 'Customer',       value: customerTotal },
    { label: 'Meta',           value: (m.meta || 0) + (m.facebook || 0) + (m.instagram || 0) },
    { label: 'Web Dimoauto',   value: e.webdimoauto || 0 },
    { label: 'Wallapop',       value: e.wallapop    || 0 },
    { label: 'Pruebas',        value: e.autoocasion || 0 },
  ].filter(x => x.value > 0).sort((a, b) => b.value - a.value);

  return items.map((it, i) => ({ ...it, color: CHART_PALETTE[i % CHART_PALETTE.length] }));
};

const buildVNVOData = (data) => ([
  { label: 'VN',  value: data.autoLeads?.leadsVN  || 0, color: '#005187' },
  { label: 'VO',  value: data.autoLeads?.leadsVO  || 0, color: '#e08c1e' },
  { label: 'B2B', value: data.autoLeads?.leadsB2B || 0, color: '#7a5bd0' },
]);

const buildCentrosData = (data) => {
  const c = data.centros || {};
  return [
    { label: 'DIMOAUTO Hospitalet',   value: c.dimoautoHospitalet  || 0, color: '#005187', colorTo: '#0a6cb0' },
    { label: 'MAAM Hospitalet',       value: c.maamHospitalet      || 0, color: '#1a9cd4', colorTo: '#6b9bd8' },
    { label: 'JP Carrión Hospitalet', value: c.jpCarrionHospitalet || 0, color: '#7cc4e8', colorTo: '#a0c8f7' },
    { label: 'DIMOAUTO Molins',       value: c.dimoautoMolins      || 0, color: '#2e9e5b', colorTo: '#34d399' },
    { label: 'JP Carrión Molins',     value: c.jpCarrionMolins     || 0, color: '#e08c1e', colorTo: '#e8ac52' },
    { label: 'MAAM Cornellà',         value: c.maamCornella        || 0, color: '#7a5bd0', colorTo: '#a78bfa' },
    { label: 'DIMOAUTO Flotas',       value: c.dimoautoFlotas      || 0, color: '#d96ba8', colorTo: '#f472b6' },
  ];
};

// Comerciales para el donut: usa el array `comercialesTop` que devuelve la RPC.
// Acorta el nombre (primer nombre + inicial del apellido) para no romper el layout.
const shortName = (full) => {
  if (!full) return '-';
  const parts = full.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[1].charAt(0)}.`;
};
// Color estable por nombre — hash determinista. Garantiza que el mismo comercial
// tenga el mismo color independientemente del orden o del periodo seleccionado.
const colorForName = (name) => {
  if (!name) return CHART_PALETTE[0];
  let h = 5381;
  for (let i = 0; i < name.length; i++) h = ((h << 5) + h + name.charCodeAt(i)) >>> 0;
  return CHART_PALETTE[h % CHART_PALETTE.length];
};
const buildComercialesData = (data) => {
  const list = Array.isArray(data.comercialesTop) ? data.comercialesTop : [];
  return list.map((c) => ({
    label: shortName(c.nombre),
    fullLabel: c.nombre,
    value: c.leads || 0,
    color: colorForName(c.nombre),
  }));
};

// Sub-card por app (fila inferior)
const AppCard = ({ title, color, hoy, semana, mes, sub, onClick }) => (
  <div
    onClick={onClick}
    style={{
      background: PAL.card,
      borderRadius: '14px',
      border: `1px solid ${PAL.border}`,
      boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
      padding: '14px 16px',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    }}
    onMouseEnter={(e) => { if (onClick) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(15,23,42,0.10)'; } }}
    onMouseLeave={(e) => { if (onClick) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(15,23,42,0.04)'; } }}
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: color }} />
        <span style={{ fontSize: '13px', fontWeight: 700, color: PAL.text }}>{title}</span>
      </div>
      <span style={{ fontSize: '11px', color: PAL.primary, fontWeight: 600 }}>Abrir →</span>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px', marginBottom: sub ? '8px' : 0 }}>
      <div style={{ textAlign: 'center', padding: '6px 4px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px' }}>
        <div style={{ fontSize: '9px', color: PAL.success, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Hoy</div>
        <div style={{ fontSize: '20px', fontWeight: 800, color: '#2e9e5b', lineHeight: 1 }}>{fmt(hoy)}</div>
      </div>
      <div style={{ textAlign: 'center', padding: '6px 4px', background: '#f4f5f7', border: `1px solid ${PAL.border}`, borderRadius: '8px' }}>
        <div style={{ fontSize: '9px', color: PAL.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Semana</div>
        <div style={{ fontSize: '20px', fontWeight: 800, color: PAL.text, lineHeight: 1 }}>{fmt(semana)}</div>
      </div>
      <div style={{ textAlign: 'center', padding: '6px 4px', background: '#f4f5f7', border: `1px solid ${PAL.border}`, borderRadius: '8px' }}>
        <div style={{ fontSize: '9px', color: PAL.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Mes</div>
        <div style={{ fontSize: '20px', fontWeight: 800, color: PAL.text, lineHeight: 1 }}>{fmt(mes)}</div>
      </div>
    </div>
    {sub && <div style={{ fontSize: '11px', color: PAL.textMuted, textAlign: 'center' }}>{sub}</div>}
  </div>
);

// ==================== DETAIL PANEL ====================

// Panel inline expandible que se renderiza debajo de la fila de tarjetas.
// Recibe un título, un icono y children con el contenido del detalle.
const DetailPanel = ({ title, icon, onClose, children, accent = PAL.primary }) => (
  <div style={{
    background: PAL.card,
    borderRadius: '14px',
    border: `1px solid ${PAL.border}`,
    boxShadow: '0 4px 18px rgba(15,23,42,0.08)',
    overflow: 'hidden',
    animation: 'dimoDetailExpand 0.25s ease-out',
  }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 18px',
      background: `linear-gradient(90deg, ${accent}, ${accent}dd)`,
      color: 'white',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {icon && <span style={{ fontSize: '18px' }}>{icon}</span>}
        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</h3>
      </div>
      <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', borderRadius: '8px', padding: '4px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>
        ✕ Cerrar
      </button>
    </div>
    <div style={{ padding: '18px' }}>{children}</div>
  </div>
);

// Tabla simple reutilizable para detalles
const DetailTable = ({ columns, rows, emptyText = 'Sin datos' }) => {
  if (!rows || rows.length === 0) {
    return <div style={{ padding: '20px', textAlign: 'center', color: PAL.textSubtle, fontSize: '12px' }}>{emptyText}</div>;
  }
  return (
    <div style={{ overflowX: 'auto', borderRadius: '10px', border: `1px solid ${PAL.border}` }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
        <thead>
          <tr style={{ background: '#f4f5f7' }}>
            {columns.map((c, i) => (
              <th key={i} style={{ textAlign: c.align || 'left', padding: '8px 12px', fontWeight: 700, color: PAL.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', fontSize: '10px', borderBottom: `1px solid ${PAL.border}` }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} style={{ borderBottom: `1px solid ${PAL.border}` }}>
              {columns.map((c, ci) => (
                <td key={ci} style={{ textAlign: c.align || 'left', padding: '8px 12px', color: c.color || PAL.text, fontWeight: c.bold ? 700 : 500 }}>
                  {c.render ? c.render(row) : row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Mini-KPI horizontal para dentro de los detalles
const MiniStat = ({ label, value, sub, color = PAL.primary, icon }) => (
  <div style={{ background: '#f4f5f7', borderRadius: '10px', padding: '10px 12px', border: `1px solid ${PAL.border}`, flex: 1, minWidth: '120px' }}>
    <div style={{ fontSize: '10px', color: PAL.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
      {icon && <span>{icon}</span>}
      {label}
    </div>
    <div style={{ fontSize: '20px', fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontSize: '11px', color: PAL.textSubtle, marginTop: '2px' }}>{sub}</div>}
  </div>
);

// ==================== HELPERS DE AGREGACIÓN PARA DETAILS ====================

// Agrupa leads por una clave y devuelve [{key, count, ...}]
const groupBy = (leads, getKey) => {
  const map = new Map();
  (leads || []).forEach(l => {
    const k = getKey(l) || '—';
    map.set(k, (map.get(k) || 0) + 1);
  });
  return Array.from(map.entries()).map(([key, count]) => ({ key, count })).sort((a, b) => b.count - a.count);
};

// Pivot: filas = grupos principales, columnas = sub-grupos. Devuelve array para tabla.
const pivot = (leads, primaryKey, secondaryKey, topN = 5) => {
  const groups = groupBy(leads, l => l[primaryKey]);
  const topSecondaries = groupBy(leads, l => l[secondaryKey]).slice(0, topN).map(x => x.key);
  return groups.map(g => {
    const subset = leads.filter(l => (l[primaryKey] || '—') === g.key);
    const row = { _key: g.key, _total: g.count };
    topSecondaries.forEach(s => {
      row[s] = subset.filter(l => (l[secondaryKey] || '—') === s).length;
    });
    return row;
  });
};

// % de leads vendidos (estado_cita == 'Vendido' o similar)
const conversionPct = (subset) => {
  if (!subset || subset.length === 0) return 0;
  const vendidos = subset.filter(l => {
    const e = (l.estado_cita || '').toLowerCase();
    return e.includes('vendido') || e === 'vendido' || e === 'venta';
  }).length;
  return Math.round((vendidos / subset.length) * 100);
};

// ==================== DETALLES POR TARJETA ====================

const CanalDetail = ({ leads, loading, channelData, totalMes }) => {
  if (loading && !leads) return <div style={{ textAlign: 'center', padding: '30px', color: PAL.textMuted, fontSize: '12px' }}><Spinner /> Cargando desglose…</div>;
  const arr = leads || [];
  const byCanal = groupBy(arr, l => l.source);
  const pivoted = pivot(arr, 'source', 'marca', 6);
  const topMarcasCols = pivoted.length > 0 ? Object.keys(pivoted[0]).filter(k => !k.startsWith('_')) : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <MiniStat icon="📈" label="Total canal" value={fmt(totalMes)} sub="leads en periodo" color={PAL.primary} />
        <MiniStat icon="📧" label="Email Leads" value={fmt(channelData[0]?.value || 0)} sub={`${Math.round(((channelData[0]?.value || 0) / Math.max(1, totalMes)) * 100)}% del total`} color={channelData[0]?.color} />
        <MiniStat icon="🤝" label="Customer Leads" value={fmt(channelData[1]?.value || 0)} sub={`${Math.round(((channelData[1]?.value || 0) / Math.max(1, totalMes)) * 100)}% del total`} color={channelData[1]?.color} />
        <MiniStat icon="📱" label="Meta Leads" value={fmt(channelData[2]?.value || 0)} sub={`${Math.round(((channelData[2]?.value || 0) / Math.max(1, totalMes)) * 100)}% del total`} color={channelData[2]?.color} />
        <MiniStat icon="🏆" label="Conversión global" value={`${conversionPct(arr)}%`} sub={`${arr.filter(l => (l.estado_cita || '').toLowerCase().includes('vendido')).length} ventas`} color={PAL.success} />
      </div>
      <div>
        <div style={{ fontSize: '11px', fontWeight: 700, color: PAL.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '8px' }}>Desglose por canal × marca (top {topMarcasCols.length} marcas)</div>
        <DetailTable
          columns={[
            { key: '_key', label: 'Canal', bold: true, render: (r) => <span style={{ color: PAL.primary, fontWeight: 700 }}>{r._key}</span> },
            { key: '_total', label: 'Total', align: 'right', bold: true },
            ...topMarcasCols.map(m => ({ key: m, label: m, align: 'right' })),
          ]}
          rows={pivoted}
        />
      </div>
      <div>
        <div style={{ fontSize: '11px', fontWeight: 700, color: PAL.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '8px' }}>Ranking de canales por conversión</div>
        <DetailTable
          columns={[
            { key: 'key', label: 'Canal', bold: true },
            { key: 'count', label: 'Leads', align: 'right' },
            { key: 'vendidos', label: 'Vendidos', align: 'right', render: (r) => <span style={{ color: PAL.success, fontWeight: 700 }}>{r.vendidos}</span> },
            { key: 'pct', label: 'Conversión', align: 'right', render: (r) => <span style={{ color: r.pct >= 5 ? PAL.success : PAL.warning, fontWeight: 700 }}>{r.pct}%</span> },
          ]}
          rows={byCanal.map(g => {
            const subset = arr.filter(l => (l.source || '—') === g.key);
            const vendidos = subset.filter(l => (l.estado_cita || '').toLowerCase().includes('vendido')).length;
            return { ...g, vendidos, pct: conversionPct(subset) };
          })}
        />
      </div>
    </div>
  );
};

const OrigenDetail = ({ leads, loading, originData }) => {
  if (loading && !leads) return <div style={{ textAlign: 'center', padding: '30px', color: PAL.textMuted, fontSize: '12px' }}><Spinner /> Cargando desglose…</div>;
  const arr = leads || [];
  const byOrigen = groupBy(arr, l => l.origen);
  const total = arr.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <MiniStat icon="🎯" label="Orígenes únicos" value={fmt(originData.length)} sub="canales de captación" color={PAL.primary} />
        <MiniStat icon="🥇" label="Top origen" value={originData[0]?.label || '—'} sub={`${originData[0]?.value || 0} leads · ${Math.round(((originData[0]?.value || 0) / Math.max(1, total)) * 100)}%`} color={originData[0]?.color || PAL.primary} />
        <MiniStat icon="📊" label="Concentración top-3" value={`${Math.round(((originData.slice(0, 3).reduce((s, o) => s + o.value, 0)) / Math.max(1, total)) * 100)}%`} sub="del total de leads" color={PAL.accent} />
        <MiniStat icon="🏆" label="Conversión global" value={`${conversionPct(arr)}%`} sub={`${arr.filter(l => (l.estado_cita || '').toLowerCase().includes('vendido')).length} ventas`} color={PAL.success} />
      </div>
      <div>
        <div style={{ fontSize: '11px', fontWeight: 700, color: PAL.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '8px' }}>Origen × marca dominante</div>
        <DetailTable
          columns={[
            { key: 'origen', label: 'Origen', bold: true, render: (r) => <span style={{ color: PAL.primary, fontWeight: 700 }}>{r.origen}</span> },
            { key: 'leads', label: 'Leads', align: 'right', bold: true },
            { key: 'marcaTop', label: 'Marca dominante', render: (r) => <span style={{ background: '#f0f6fa', color: PAL.primary, padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 700 }}>{r.marcaTop}</span> },
            { key: 'vendidos', label: 'Ventas', align: 'right', render: (r) => <span style={{ color: PAL.success, fontWeight: 700 }}>{r.vendidos}</span> },
            { key: 'pct', label: 'Conv.', align: 'right', render: (r) => <span style={{ color: r.pct >= 5 ? PAL.success : PAL.warning, fontWeight: 700 }}>{r.pct}%</span> },
          ]}
          rows={byOrigen.map(g => {
            const subset = arr.filter(l => (l.origen || '—') === g.key);
            const marcas = groupBy(subset, l => l.marca);
            const vendidos = subset.filter(l => (l.estado_cita || '').toLowerCase().includes('vendido')).length;
            return {
              origen: g.key,
              leads: g.count,
              marcaTop: marcas[0]?.key || '—',
              vendidos,
              pct: conversionPct(subset),
            };
          })}
        />
      </div>
    </div>
  );
};

const TipoVentaDetail = ({ leads, loading, data }) => {
  if (loading && !leads) return <div style={{ textAlign: 'center', padding: '30px', color: PAL.textMuted, fontSize: '12px' }}><Spinner /> Cargando desglose…</div>;
  const arr = leads || [];
  const tipos = ['VN', 'VO', 'B2B'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        {tipos.map(t => {
          const subset = arr.filter(l => {
            const tv = (l.tipo_venta || '').toUpperCase();
            return tv === t || (t === 'B2B' && tv.includes('B2B'));
          });
          const color = t === 'VN' ? PAL.primary : t === 'VO' ? PAL.warning : '#7a5bd0';
          return <MiniStat key={t} icon={t === 'VN' ? '🆕' : t === 'VO' ? '🚙' : '🏢'} label={`Tipo ${t}`} value={fmt(subset.length)} sub={`${conversionPct(subset)}% conversión`} color={color} />;
        })}
        <MiniStat icon="⏳" label="Pendientes" value={fmt(data.autoLeads.pendientes || 0)} sub="sin asignar" color={PAL.warning} />
      </div>
      {tipos.map(t => {
        const subset = arr.filter(l => {
          const tv = (l.tipo_venta || '').toUpperCase();
          return tv === t || (t === 'B2B' && tv.includes('B2B'));
        });
        const porMarca = groupBy(subset, l => l.marca).slice(0, 10);
        if (porMarca.length === 0) return null;
        return (
          <div key={t}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: PAL.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '8px' }}>
              Top marcas — <span style={{ color: t === 'VN' ? PAL.primary : t === 'VO' ? PAL.warning : '#7a5bd0' }}>{t}</span> ({subset.length} leads)
            </div>
            <DetailTable
              columns={[
                { key: 'key', label: 'Marca', bold: true },
                { key: 'count', label: 'Leads', align: 'right', bold: true },
                { key: 'pct', label: '% del tipo', align: 'right', render: (r) => `${Math.round((r.count / Math.max(1, subset.length)) * 100)}%` },
              ]}
              rows={porMarca}
            />
          </div>
        );
      })}
    </div>
  );
};

const CentrosDetail = ({ leads, loading, centrosData, comercialesMap }) => {
  if (loading && !leads) return <div style={{ textAlign: 'center', padding: '30px', color: PAL.textMuted, fontSize: '12px' }}><Spinner /> Cargando desglose…</div>;
  const arr = leads || [];
  const sorted = [...centrosData].sort((a, b) => b.value - a.value);
  const top = sorted[0];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <MiniStat icon="🏢" label="Centros activos" value={fmt(sorted.filter(c => c.value > 0).length)} sub={`de ${sorted.length} centros`} color={PAL.primary} />
        <MiniStat icon="🥇" label="Centro top" value={top?.label.split(' ')[0]} sub={`${top?.value || 0} leads · ${Math.round(((top?.value || 0) / Math.max(1, arr.length)) * 100)}%`} color={top?.color} />
        <MiniStat icon="🏆" label="Conversión global" value={`${conversionPct(arr)}%`} sub={`${arr.filter(l => (l.estado_cita || '').toLowerCase().includes('vendido')).length} ventas`} color={PAL.success} />
      </div>
      <div>
        <div style={{ fontSize: '11px', fontWeight: 700, color: PAL.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '8px' }}>Desglose por centro</div>
        <DetailTable
          columns={[
            { key: 'centro', label: 'Centro', bold: true, render: (r) => <span style={{ color: PAL.primary, fontWeight: 700 }}>{r.centro}</span> },
            { key: 'leads', label: 'Leads', align: 'right', bold: true },
            { key: 'comercialesActivos', label: 'Comerciales activos', align: 'right' },
            { key: 'marcaTop', label: 'Marca dominante', render: (r) => <span style={{ background: '#f0f6fa', color: PAL.primary, padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 700 }}>{r.marcaTop}</span> },
            { key: 'vendidos', label: 'Ventas', align: 'right', render: (r) => <span style={{ color: PAL.success, fontWeight: 700 }}>{r.vendidos}</span> },
            { key: 'pct', label: 'Conv.', align: 'right', render: (r) => <span style={{ color: r.pct >= 5 ? PAL.success : PAL.warning, fontWeight: 700 }}>{r.pct}%</span> },
          ]}
          rows={sorted.filter(c => c.value > 0).map(c => {
            const subset = arr.filter(l => (l.centro || '') === c.label);
            const marcas = groupBy(subset, l => l.marca);
            const comerciales = new Set(subset.filter(l => l.vendedor_nombre).map(l => l.vendedor_nombre));
            const vendidos = subset.filter(l => (l.estado_cita || '').toLowerCase().includes('vendido')).length;
            return {
              centro: c.label,
              leads: c.value,
              comercialesActivos: comerciales.size,
              marcaTop: marcas[0]?.key || '—',
              vendidos,
              pct: conversionPct(subset),
            };
          })}
          emptyText="Sin leads asignados a centros en este periodo"
        />
      </div>
    </div>
  );
};

const ComercialesDetail = ({ leads, loading, comercialesData, comercialesMap }) => {
  if (loading && !leads) return <div style={{ textAlign: 'center', padding: '30px', color: PAL.textMuted, fontSize: '12px' }}><Spinner /> Cargando desglose…</div>;
  const arr = leads || [];
  const total = comercialesData.reduce((s, c) => s + c.value, 0);
  const top = comercialesData.slice().sort((a, b) => b.value - a.value)[0];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <MiniStat icon="👥" label="Comerciales activos" value={fmt(comercialesData.length)} sub="con asignaciones" color={PAL.primary} />
        <MiniStat icon="🥇" label="Comercial top" value={top?.label || '—'} sub={`${top?.value || 0} leads · ${Math.round(((top?.value || 0) / Math.max(1, total)) * 100)}%`} color={top?.color} />
        <MiniStat icon="⚖️" label="Reparto medio" value={fmt(Math.round(total / Math.max(1, comercialesData.length)))} sub="leads por comercial" color={PAL.accent} />
        <MiniStat icon="🏆" label="Conversión global" value={`${conversionPct(arr)}%`} sub={`${arr.filter(l => (l.estado_cita || '').toLowerCase().includes('vendido')).length} ventas`} color={PAL.success} />
      </div>
      <div>
        <div style={{ fontSize: '11px', fontWeight: 700, color: PAL.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '8px' }}>Ranking detallado por comercial</div>
        <DetailTable
          columns={[
            { key: 'nombre', label: 'Comercial', bold: true, render: (r) => <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}><span style={{ width: '10px', height: '10px', borderRadius: '50%', background: r.color, display: 'inline-block' }} /><span style={{ fontWeight: 700 }}>{r.nombre}</span></span> },
            { key: 'leads', label: 'Asignados', align: 'right', bold: true },
            { key: 'pct', label: '% reparto', align: 'right', render: (r) => `${Math.round((r.leads / Math.max(1, total)) * 100)}%` },
            { key: 'marcaTop', label: 'Marca + frecuente', render: (r) => <span style={{ background: '#f0f6fa', color: PAL.primary, padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 700 }}>{r.marcaTop}</span> },
            { key: 'vendidos', label: 'Ventas', align: 'right', render: (r) => <span style={{ color: PAL.success, fontWeight: 700 }}>{r.vendidos}</span> },
            { key: 'conv', label: 'Conversión', align: 'right', render: (r) => <span style={{ color: r.conv >= 5 ? PAL.success : PAL.warning, fontWeight: 700 }}>{r.conv}%</span> },
          ]}
          rows={comercialesData.map(c => {
            // leads.vendedor_nombre es el nombre del comercial; comparamos directo
            // contra c.fullLabel (antes se buscaba id en comercialesMap, pero
            // leads ya no expone comercial_id — el subset siempre quedaba vacío).
            const subset = c.fullLabel ? arr.filter(l => (l.vendedor_nombre || '').trim() === c.fullLabel.trim()) : [];
            const marcas = groupBy(subset, l => l.marca);
            const vendidos = subset.filter(l => (l.estado_cita || '').toLowerCase().includes('vendido')).length;
            return {
              nombre: c.fullLabel || c.label,
              color: c.color,
              leads: c.value,
              marcaTop: marcas[0]?.key || '—',
              vendidos,
              conv: conversionPct(subset),
            };
          }).sort((a, b) => b.leads - a.leads)}
          emptyText="Sin asignaciones en el periodo"
        />
      </div>
    </div>
  );
};

// ==================== LIVE LOG FEED ====================

// Formatea un timestamp relativo "hace 2 min", "hace 5 seg"
const relTime = (ts) => {
  if (!ts) return '—';
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 5) return 'ahora';
  if (diff < 60) return `hace ${Math.round(diff)}s`;
  if (diff < 3600) return `hace ${Math.round(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.round(diff / 3600)} h`;
  return `hace ${Math.round(diff / 86400)} días`;
};

const LIVE_LOG_POLL_MS = 15000;
const LIVE_LOG_LIMIT = 30;

const LiveLogFeed = ({ comercialesMap }) => {
  const [events, setEvents] = useState([]); // {id, type, ts, lead, color, icon, message, sub}
  const [paused, setPaused] = useState(false);
  const [filter, setFilter] = useState('all'); // all / new / assigned / state
  const [tick, setTick] = useState(0);
  const knownLeadsRef = React.useRef(new Map()); // id -> {vendedor_nombre, estado_cita, numero}
  const firstLoadRef = React.useRef(true);

  // Tick para refrescar timestamps relativos
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const pushEvent = useCallback((ev) => {
    setEvents(prev => [ev, ...prev].slice(0, 50));
  }, []);

  const formatLeadNum = (n) => {
    if (!n && n !== 0) return 'L-?????';
    const s = String(n);
    // lead_id es TEXT: aplicamos padStart sólo si es puramente numérico;
    // los lead_id anómalos (p.ej. '00renamed-…') se muestran tal cual con prefijo.
    if (/^[0-9]+$/.test(s)) return `L-${s.padStart(5, '0')}`;
    return `L-${s}`;
  };

  const tryFetch = useCallback(async () => {
    if (paused) return;
    try {
      const url = `${SUPABASE_URL}/rest/v1/leads?select=id,lead_id,marca,modelo,source,origen,tipo_venta,estado_cita,vendedor_nombre,fecha_creacion&order=fecha_creacion.desc&limit=${LIVE_LOG_LIMIT}`;
      const r = await fetch(url, { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` }});
      if (!r.ok) return;
      const rows = await r.json();
      const known = knownLeadsRef.current;
      const newEvents = [];

      rows.forEach(row => {
        const prev = known.get(row.id);
        if (!prev) {
          // Primer poll: sembramos eventos como histórico sin notificar
          if (!firstLoadRef.current) {
            newEvents.push({
              id: `new-${row.id}`,
              type: 'new',
              ts: row.fecha_creacion || new Date().toISOString(),
              color: '#005187',
              icon: '🆕',
              message: `Nuevo lead ${formatLeadNum(row.lead_id)} · ${row.marca || '—'} ${row.modelo || ''}`.trim(),
              sub: `${row.source || '—'} · ${row.origen || '—'}`,
            });
          }
        } else {
          // Detectar asignación nueva
          if (!prev.vendedor_nombre && row.vendedor_nombre) {
            const nombre = row.vendedor_nombre || 'comercial';
            newEvents.push({
              id: `assign-${row.id}-${row.vendedor_nombre}`,
              type: 'assigned',
              ts: new Date().toISOString(),
              color: '#2e9e5b',
              icon: '🎯',
              message: `${formatLeadNum(row.lead_id)} asignado a ${nombre}`,
              sub: `${row.marca || ''} ${row.tipo_venta || ''}`.trim(),
            });
          } else if (prev.vendedor_nombre && row.vendedor_nombre && prev.vendedor_nombre !== row.vendedor_nombre) {
            const nombre = row.vendedor_nombre || 'comercial';
            newEvents.push({
              id: `reassign-${row.id}-${row.vendedor_nombre}-${Date.now()}`,
              type: 'assigned',
              ts: new Date().toISOString(),
              color: '#1a9cd4',
              icon: '🔄',
              message: `${formatLeadNum(row.lead_id)} reasignado a ${nombre}`,
              sub: `${row.marca || ''} ${row.tipo_venta || ''}`.trim(),
            });
          }
          // Detectar cambio de estado
          if (prev.estado_cita && prev.estado_cita !== row.estado_cita) {
            const lower = (row.estado_cita || '').toLowerCase();
            const isVenta = lower.includes('vendido') || lower === 'venta';
            const isDescartado = lower.includes('descart') || lower.includes('perdido');
            const isAlert = isDescartado;
            newEvents.push({
              id: `state-${row.id}-${row.estado_cita}-${Date.now()}`,
              type: isVenta ? 'sale' : isAlert ? 'warn' : 'state',
              ts: new Date().toISOString(),
              color: isVenta ? '#2e9e5b' : isAlert ? '#d23b34' : '#e08c1e',
              icon: isVenta ? '💰' : isAlert ? '⚠️' : '🔁',
              message: `${formatLeadNum(row.lead_id)} → ${row.estado_cita || 'sin estado'}`,
              sub: `${row.marca || ''} ${row.modelo || ''}`.trim(),
            });
          }
        }
        known.set(row.id, { vendedor_nombre: row.vendedor_nombre, estado_cita: row.estado_cita, numero: row.lead_id });
      });

      // Primera carga: sembrar feed con los últimos eventos como histórico (sin badge "NUEVO")
      if (firstLoadRef.current) {
        firstLoadRef.current = false;
        const seeded = rows.slice(0, 15).map((row, idx) => ({
          id: `seed-${row.id}`,
          type: 'new',
          ts: row.fecha_creacion,
          color: '#005187',
          icon: '🆕',
          message: `Lead ${formatLeadNum(row.lead_id)} · ${row.marca || '—'} ${row.modelo || ''}`.trim(),
          sub: `${row.source || '—'} · ${row.origen || '—'}${row.vendedor_nombre ? ` · ${row.vendedor_nombre || ''}` : ''}`,
          seed: true,
        }));
        setEvents(seeded);
      } else if (newEvents.length > 0) {
        newEvents.reverse().forEach(pushEvent);
      }
    } catch (e) {
      console.error('LiveLogFeed fetch error:', e);
    }
  }, [paused, comercialesMap, pushEvent]);

  useEffect(() => {
    tryFetch();
    const t = setInterval(tryFetch, LIVE_LOG_POLL_MS);
    return () => clearInterval(t);
  }, [tryFetch]);

  const filtered = events.filter(e => {
    if (filter === 'all') return true;
    if (filter === 'new') return e.type === 'new';
    if (filter === 'assigned') return e.type === 'assigned';
    if (filter === 'state') return e.type === 'state' || e.type === 'sale' || e.type === 'warn';
    return true;
  });

  const countNuevos = events.filter(e => !e.seed).length;

  return (
    <div style={{ background: PAL.card, borderRadius: '14px', border: `1px solid ${PAL.border}`, boxShadow: '0 1px 3px rgba(15,23,42,0.04)', overflow: 'hidden' }}>
      <div style={{ padding: '12px 18px', borderBottom: `1px solid ${PAL.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: paused ? '#fbf1de' : '#e6f4ec', color: paused ? '#a8660f' : '#1f7a44', padding: '3px 10px', borderRadius: '12px', fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: paused ? '#e08c1e' : '#2e9e5b', animation: paused ? 'none' : 'dimoPulse 1.4s ease-in-out infinite' }} />
            {paused ? 'En pausa' : 'En vivo'}
          </span>
          <h3 style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: PAL.text, textTransform: 'uppercase', letterSpacing: '0.4px' }}>📡 Actividad del CRM</h3>
          <span style={{ fontSize: '11px', color: PAL.textSubtle }}>{countNuevos} eventos · poll cada {LIVE_LOG_POLL_MS / 1000}s</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {[
            { id: 'all', label: 'Todo', color: PAL.text },
            { id: 'new', label: '🆕 Nuevos', color: '#005187' },
            { id: 'assigned', label: '🎯 Asignados', color: '#2e9e5b' },
            { id: 'state', label: '💰 Estado', color: '#e08c1e' },
          ].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)} style={{
              border: `1px solid ${filter === f.id ? f.color : PAL.border}`,
              background: filter === f.id ? f.color : 'white',
              color: filter === f.id ? 'white' : PAL.textMuted,
              borderRadius: '999px', padding: '3px 10px', fontSize: '10px', fontWeight: 700, cursor: 'pointer',
              transition: 'all 0.15s',
            }}>{f.label}</button>
          ))}
          <button onClick={() => setPaused(p => !p)} style={{ border: `1px solid ${PAL.border}`, background: 'white', color: PAL.textMuted, borderRadius: '999px', padding: '3px 10px', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}>
            {paused ? '▶ Reanudar' : '⏸ Pausar'}
          </button>
        </div>
      </div>
      <div style={{ maxHeight: '300px', overflowY: 'auto', padding: '4px 0' }}>
        {filtered.length === 0 && (
          <div style={{ padding: '24px', textAlign: 'center', color: PAL.textSubtle, fontSize: '12px' }}>
            Esperando eventos en el CRM…
          </div>
        )}
        {filtered.map((ev, i) => (
          <div key={ev.id} style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            padding: '8px 18px',
            borderBottom: i < filtered.length - 1 ? `1px solid ${PAL.border}` : 'none',
            animation: !ev.seed && i === 0 ? 'dimoLogIn 0.4s ease-out' : 'none',
            background: !ev.seed && i === 0 ? '#f3faf6' : 'transparent',
            transition: 'background 0.6s ease',
          }}>
            <span style={{ fontSize: '16px', flexShrink: 0, lineHeight: 1.3 }}>{ev.icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', color: PAL.text, fontWeight: 600, lineHeight: 1.3 }}>{ev.message}</div>
              {ev.sub && <div style={{ fontSize: '11px', color: PAL.textMuted, marginTop: '2px' }}>{ev.sub}</div>}
            </div>
            <span style={{ fontSize: '10px', color: PAL.textSubtle, flexShrink: 0, fontWeight: 600, whiteSpace: 'nowrap' }} title={ev.ts}>{relTime(ev.ts)}{tick >= 0 ? '' : ''}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== DASHBOARD PRINCIPAL ====================

function Dashboard() {
  const isMobile = useIsMobile();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Filtro de fechas (vacío = default "mes en curso" según RPC)
  const [fromDate, setFromDate] = useState(monthStartISO());
  const [toDate,   setToDate]   = useState(todayISO());
  const [data, setData] = useState({
    emailLeads: { hoy: 0, semana: 0, mes: 0, tiempoMedio: '-', origenes: {} },
    customerLeads: { hoy: 0, semana: 0, mes: 0, tiempoMedio: '-', origenes: {} },
    metaLeads: { hoy: 0, semana: 0, mes: 0, tiempoMedio: '-', origenes: {} },
    autoLeads: { hoy: 0, semana: 0, mes: 0, pendientes: 0, ultimaAsignacion: 'N/A', leadsVN: 0, leadsVO: 0, leadsPorComercial: 0, tiempoMedioGestion: '-' },
    crmAdmin: { admins: 0, leads: 0, vn: 0, vo: 0, vendidos: 0 },
    crmComerciales: { activos: 0, asignados: 0, tasaConversion: 0, tiempoRespuesta: '-' },
    centros: {},
    lastUpdate: null,
  });

  // ===== Detail panel state =====
  const [expandedDetail, setExpandedDetail] = useState(null); // 'canal' | 'origen' | 'tipoventa' | 'centros' | 'comerciales'
  const [detailLeads, setDetailLeads] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [comercialesMap, setComercialesMap] = useState({}); // id -> "Nombre Apellido"

  const toggleDetail = (key) => setExpandedDetail(prev => prev === key ? null : key);

  // Invalidar caché del detalle al cambiar el periodo
  useEffect(() => { setDetailLeads(null); }, [fromDate, toDate]);

  // Cargar mapa de comerciales una sola vez (necesario para el LiveLogFeed)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${SUPABASE_URL}/rest/v1/comerciales?select=id,nombre,apellidos&limit=200`, {
          headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` }
        });
        if (!r.ok || cancelled) return;
        const arr = await r.json();
        const map = {};
        arr.forEach(c => { map[c.id] = `${c.nombre || ''} ${c.apellidos || ''}`.trim(); });
        if (!cancelled) setComercialesMap(map);
      } catch (e) { console.error('comerciales fetch error:', e); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch on-demand de los leads del periodo para los paneles de detalle
  const fetchDetailLeads = useCallback(async () => {
    setDetailLoading(true);
    try {
      const fromTs = encodeURIComponent(toRpcFrom(fromDate));
      const toTs   = encodeURIComponent(toRpcTo(toDate));
      const url = `${SUPABASE_URL}/rest/v1/leads?select=id,lead_id,marca,modelo,source,origen,tipo_venta,centro,estado_cita,fecha_creacion,vendedor_nombre&fecha_creacion=gte.${fromTs}&fecha_creacion=lte.${toTs}&order=fecha_creacion.desc&limit=2000`;
      const r = await fetch(url, { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` }});
      const rows = r.ok ? await r.json() : [];
      setDetailLeads(rows);

      // Comerciales (id -> nombre) — solo si no se ha cargado todavía
      if (Object.keys(comercialesMap).length === 0) {
        const r2 = await fetch(`${SUPABASE_URL}/rest/v1/comerciales?select=id,nombre,apellidos&limit=100`, { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` }});
        if (r2.ok) {
          const arr = await r2.json();
          const map = {};
          arr.forEach(c => { map[c.id] = `${c.nombre || ''} ${c.apellidos || ''}`.trim(); });
          setComercialesMap(map);
        }
      }
    } catch (e) {
      console.error('Detail leads fetch error:', e);
    } finally {
      setDetailLoading(false);
    }
  }, [fromDate, toDate, comercialesMap]);

  // Cuando se abre un detalle y no hay datos en caché, dispara fetch
  useEffect(() => {
    if (expandedDetail && detailLeads === null && !detailLoading) {
      fetchDetailLeads();
    }
  }, [expandedDetail, detailLeads, detailLoading, fetchDetailLeads]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const rpcData = await fetchDashboardStats(toRpcFrom(fromDate), toRpcTo(toDate));
    if (rpcData) {
      setData(rpcData);
    } else {
      setError('No se pudieron cargar los datos. Reintentando en 5 min.');
    }
    setIsLoading(false);
  }, [fromDate, toDate]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, REFRESH_MS);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleMesEnCurso = () => {
    setFromDate(monthStartISO());
    setToDate(todayISO());
  };
  const isMesEnCurso = fromDate === monthStartISO() && toDate === todayISO();

  const handleOpenApp = (appId) => {
    const event = new CustomEvent('openApp', { detail: appId });
    window.dispatchEvent(event);
  };

  const channelData = useMemo(() => buildChannelData(data), [data]);
  const originData  = useMemo(() => buildOriginData(data),  [data]);
  const vnvoData    = useMemo(() => buildVNVOData(data),    [data]);
  const centrosData     = useMemo(() => buildCentrosData(data),     [data]);
  const comercialesData = useMemo(() => buildComercialesData(data), [data]);

  const totalMes = (data.emailLeads?.mes || 0) + (data.customerLeads?.mes || 0) + (data.metaLeads?.mes || 0);

  // ============ MÓVIL ============
  if (isMobile) {
    return <MobileDashboard data={data} isLoading={isLoading} error={error} onOpenApp={handleOpenApp}
                            channelData={channelData} originData={originData} vnvoData={vnvoData} centrosData={centrosData} comercialesData={comercialesData} totalMes={totalMes}
                            fromDate={fromDate} toDate={toDate} setFromDate={setFromDate} setToDate={setToDate}
                            handleMesEnCurso={handleMesEnCurso} isMesEnCurso={isMesEnCurso}
                            expandedDetail={expandedDetail} toggleDetail={toggleDetail} setExpandedDetail={setExpandedDetail}
                            detailLeads={detailLeads} detailLoading={detailLoading} comercialesMap={comercialesMap} />;
  }

  // ============ DESKTOP ============
  return (
    <div className="fg-fade" style={{ background: PAL.bg, minHeight: '100%', padding: '14px 18px 24px', boxSizing: 'border-box' }}>
      <style>{`
        @keyframes dimoSpin { to { transform: rotate(360deg); } }
        @keyframes dimoPulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(1.4); } }
        @keyframes dimoDetailExpand { from { opacity: 0; transform: translateY(-8px); max-height: 0; } to { opacity: 1; transform: translateY(0); max-height: 2000px; } }
        @keyframes dimoLogIn { from { opacity: 0; transform: translateX(-12px); background: #d3ecdd; } to { opacity: 1; transform: translateX(0); background: #f3faf6; } }
      `}</style>

      {/* HEADER */}
      <div style={{ maxWidth: '1400px', margin: '0 auto 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'white', borderRadius: '14px', padding: '8px 18px', border: `1px solid ${PAL.border}`, boxShadow: '0 1px 3px rgba(15,23,42,0.04)', gap: '14px', flexWrap: 'wrap' }}>
        {/* Sin logotipo: la marca la pone la cáscara (barra superior) o el menú
            lateral, y repetirla aquí era el segundo logotipo en pantalla. */}
        <div>
          <h1 style={{ fontSize: '19px', fontWeight: 800, margin: 0, color: PAL.text, letterSpacing: '-0.2px' }}>Resumen</h1>
          <div style={{ fontSize: '12.5px', color: PAL.textMuted, marginTop: '3px' }}>Todos los leads del grupo, de dónde vienen y quién los lleva</div>
        </div>

        {/* Selector de fechas discreto */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: PAL.textMuted, background: '#f4f5f7', border: `1px solid ${PAL.border}`, borderRadius: '10px', padding: '4px 8px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: PAL.textSubtle, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Periodo</span>
          <input
            type="date"
            value={fromDate}
            max={toDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{ border: 'none', background: 'transparent', fontSize: '12px', color: PAL.text, fontFamily: 'inherit', padding: '2px 4px', outline: 'none', cursor: 'pointer' }}
          />
          <span style={{ color: PAL.textSubtle }}>→</span>
          <input
            type="date"
            value={toDate}
            min={fromDate}
            max={todayISO()}
            onChange={(e) => setToDate(e.target.value)}
            style={{ border: 'none', background: 'transparent', fontSize: '12px', color: PAL.text, fontFamily: 'inherit', padding: '2px 4px', outline: 'none', cursor: 'pointer' }}
          />
          <button
            onClick={handleMesEnCurso}
            style={{
              marginLeft: '4px',
              border: `1px solid ${isMesEnCurso ? PAL.primary : PAL.border}`,
              background: isMesEnCurso ? PAL.primary : 'white',
              color: isMesEnCurso ? 'white' : PAL.primary,
              borderRadius: '8px',
              padding: '4px 10px',
              fontSize: '11px',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              whiteSpace: 'nowrap',
            }}
            title="Restablecer al mes en curso"
          >
            Mes en curso
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', color: PAL.textMuted }}>
          {isLoading ? <Spinner /> : <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: error ? PAL.danger : PAL.success }} />}
          <span>{isLoading ? 'Actualizando…' : error ? 'Error de conexión' : `Última actualización ${data.lastUpdate || '-'}`}</span>
          <span style={{ color: PAL.textSubtle, fontSize: '11px' }}>· auto-refresh 5 min</span>
        </div>
      </div>

      {/* FILA SALUD — lo primero que se ve: qué está roto ahora mismo */}
      <div style={{ maxWidth: '1400px', margin: '0 auto 12px' }}>
        <PanelSalud />
      </div>

      {/* FILA KPIs */}
      <div style={{ maxWidth: '1400px', margin: '0 auto 12px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
        <KPICard label="Total Leads CRM"     value={fmt(data.crmAdmin.leads)}                  sub={`${fmt(data.crmAdmin.vn)} VN · ${fmt(data.crmAdmin.vo)} VO · ${fmt(data.crmAdmin.b2b || 0)} B2B`} color={PAL.primary} />
        <KPICard label={isMesEnCurso ? 'AutoLeads este mes' : 'AutoLeads en periodo'}  value={fmt(data.autoLeads.mes)}                   sub={`${fmt(data.autoLeads.hoy)} hoy · ${fmt(data.autoLeads.semana)} semana`} color={PAL.accent} />
        <KPICard label="Tasa de conversión"  value={`${data.crmComerciales.tasaConversion}%`}  sub={`${fmt(data.crmAdmin.vendidosPeriodo ?? data.crmAdmin.vendidos)} vendidos / ${fmt(data.crmComerciales.asignados)} asignados`} color={PAL.success} />
        <KPICard label="Comerciales activos" value={fmt(data.crmComerciales.activos)}          sub={`Última asignación: ${data.autoLeads.ultimaAsignacion}`} color={PAL.warning} />
      </div>

      {/* FILA GRÁFICOS */}
      <div style={{ maxWidth: '1400px', margin: '0 auto 12px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
        {/* Canal */}
        <ChartCard title={isMesEnCurso ? 'Leads por canal (mes)' : 'Leads por canal (periodo)'} icon="📊" action={expandedDetail === 'canal' ? 'Cerrar' : 'Detalle'} onClick={() => toggleDetail('canal')} active={expandedDetail === 'canal'}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <DonutChart data={channelData} size={170} thickness={26} centerLabel="Total" centerValue={fmt(totalMes)} />
            <div style={{ flex: 1 }}>
              <Legend data={channelData} />
            </div>
          </div>
        </ChartCard>

        {/* Orígenes */}
        <ChartCard title={isMesEnCurso ? 'Origen del lead (mes)' : 'Origen del lead (periodo)'} icon="🎯" action={expandedDetail === 'origen' ? 'Cerrar' : 'Detalle'} onClick={() => toggleDetail('origen')} active={expandedDetail === 'origen'}>
          {originData.length === 0 ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: PAL.textSubtle, fontSize: '12px' }}>Sin datos este mes</div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <DonutChart data={originData} size={170} thickness={26} centerLabel="Orígenes" centerValue={originData.length} />
              <div style={{ flex: 1 }}>
                <Legend data={originData.slice(0, 6)} compact />
              </div>
            </div>
          )}
        </ChartCard>

        {/* VN / VO / B2B */}
        <ChartCard title={isMesEnCurso ? 'Tipo de venta (mes)' : 'Tipo de venta (periodo)'} icon="🚗" action={expandedDetail === 'tipoventa' ? 'Cerrar' : 'Detalle'} onClick={() => toggleDetail('tipoventa')} active={expandedDetail === 'tipoventa'}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <DonutChart data={vnvoData} size={170} thickness={26} centerLabel="Total" centerValue={fmt((data.autoLeads.leadsVN || 0) + (data.autoLeads.leadsVO || 0) + (data.autoLeads.leadsB2B || 0))} />
            <div style={{ flex: 1 }}>
              <Legend data={vnvoData} />
              <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: `1px dashed ${PAL.border}`, fontSize: '11px', color: PAL.textMuted }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span>Pendientes</span>
                  <span style={{ color: data.autoLeads.pendientes > 0 ? PAL.warning : PAL.success, fontWeight: 700 }}>{fmt(data.autoLeads.pendientes)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Por comercial</span>
                  <span style={{ color: PAL.text, fontWeight: 700 }}>{fmt(data.autoLeads.leadsPorComercial)}</span>
                </div>
              </div>
              <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: `1px dashed ${PAL.border}`, fontSize: '10px', color: PAL.textSubtle }}>
                <div style={{ fontWeight: 700, color: PAL.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '4px' }}>CRM histórico</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '6px' }}>
                  <span><span style={{ color: '#005187', fontWeight: 700 }}>VN</span> {fmt(data.crmAdmin.vn)}</span>
                  <span><span style={{ color: '#e08c1e', fontWeight: 700 }}>VO</span> {fmt(data.crmAdmin.vo)}</span>
                  <span><span style={{ color: '#7a5bd0', fontWeight: 700 }}>B2B</span> {fmt(data.crmAdmin.b2b || 0)}</span>
                </div>
              </div>
            </div>
          </div>
        </ChartCard>
      </div>

      {/* DETALLE INLINE para canal / origen / tipoventa */}
      {(expandedDetail === 'canal' || expandedDetail === 'origen' || expandedDetail === 'tipoventa') && (
        <div style={{ maxWidth: '1400px', margin: '0 auto 12px' }}>
          {expandedDetail === 'canal' && (
            <DetailPanel title="Leads por canal — desglose" icon="📊" accent={PAL.primary} onClose={() => setExpandedDetail(null)}>
              <CanalDetail leads={detailLeads} loading={detailLoading} channelData={channelData} totalMes={totalMes} />
            </DetailPanel>
          )}
          {expandedDetail === 'origen' && (
            <DetailPanel title="Origen del lead — desglose" icon="🎯" accent={PAL.accent} onClose={() => setExpandedDetail(null)}>
              <OrigenDetail leads={detailLeads} loading={detailLoading} originData={originData} />
            </DetailPanel>
          )}
          {expandedDetail === 'tipoventa' && (
            <DetailPanel title="Tipo de venta — desglose" icon="🚗" accent="#e08c1e" onClose={() => setExpandedDetail(null)}>
              <TipoVentaDetail leads={detailLeads} loading={detailLoading} data={data} />
            </DetailPanel>
          )}
        </div>
      )}

      {/* FILA CENTROS + COMERCIALES (2 columnas simétricas) */}
      <div style={{ maxWidth: '1400px', margin: '0 auto 12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <ChartCard title={isMesEnCurso ? 'Leads por centro (mes)' : 'Leads por centro (periodo)'} icon="🏢" action={expandedDetail === 'centros' ? 'Cerrar' : 'Detalle'} onClick={() => toggleDetail('centros')} active={expandedDetail === 'centros'}>
          <HorizontalBars data={centrosData} height={20} />
        </ChartCard>
        <ChartCard title={isMesEnCurso ? 'Asignaciones por comercial (mes)' : 'Asignaciones por comercial (periodo)'} icon="👥" action={expandedDetail === 'comerciales' ? 'Cerrar' : 'Detalle'} onClick={() => toggleDetail('comerciales')} active={expandedDetail === 'comerciales'}>
          {comercialesData.length === 0 ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: PAL.textSubtle, fontSize: '12px' }}>Sin asignaciones en el periodo</div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <DonutChart data={comercialesData} size={180} thickness={28} centerLabel="Total" centerValue={fmt(comercialesData.reduce((s, d) => s + d.value, 0))} />
              <div style={{ flex: 1 }}>
                <Legend data={comercialesData} compact />
              </div>
            </div>
          )}
        </ChartCard>
      </div>

      {/* DETALLE INLINE para centros / comerciales */}
      {(expandedDetail === 'centros' || expandedDetail === 'comerciales') && (
        <div style={{ maxWidth: '1400px', margin: '0 auto 12px' }}>
          {expandedDetail === 'centros' && (
            <DetailPanel title="Leads por centro — desglose" icon="🏢" accent="#2e9e5b" onClose={() => setExpandedDetail(null)}>
              <CentrosDetail leads={detailLeads} loading={detailLoading} centrosData={centrosData} comercialesMap={comercialesMap} />
            </DetailPanel>
          )}
          {expandedDetail === 'comerciales' && (
            <DetailPanel title="Asignaciones por comercial — desglose" icon="👥" accent="#7a5bd0" onClose={() => setExpandedDetail(null)}>
              <ComercialesDetail leads={detailLeads} loading={detailLoading} comercialesData={comercialesData} comercialesMap={comercialesMap} />
            </DetailPanel>
          )}
        </div>
      )}

      {/* FILA APPS */}
      <div style={{ maxWidth: '1400px', margin: '0 auto 12px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
        <AppCard title="Email Leads"     color="#005187" hoy={data.emailLeads.hoy}    semana={data.emailLeads.semana}    mes={data.emailLeads.mes}    sub={`Carnovo ${data.emailLeads.origenes?.carnovo || 0} · Coches.net VN ${data.emailLeads.origenes?.cochesnetVN || 0} · VO ${data.emailLeads.origenes?.cochesnetVO || 0}`} onClick={() => handleOpenApp('email-leads')} />
        <AppCard title="Customer Leads"  color="#1a9cd4" hoy={data.customerLeads.hoy} semana={data.customerLeads.semana} mes={data.customerLeads.mes} sub={`Citroën ${data.customerLeads.origenes?.citroen || 0} · Peugeot ${data.customerLeads.origenes?.peugeot || 0}`} onClick={() => handleOpenApp('customer-leads')} />
        <AppCard title="Meta Leads"      color="#7cc4e8" hoy={data.metaLeads.hoy}     semana={data.metaLeads.semana}     mes={data.metaLeads.mes}     sub={`Meta: ${fmt((data.metaLeads.origenes?.meta || 0) + (data.metaLeads.origenes?.facebook || 0) + (data.metaLeads.origenes?.instagram || 0))} total en periodo`} onClick={() => handleOpenApp('fb-meta-leads')} />
        <AppCard title="AutoLeads"       color="#2e9e5b" hoy={data.autoLeads.hoy}     semana={data.autoLeads.semana}     mes={data.autoLeads.mes}     sub={`${fmt(data.autoLeads.pendientes)} pendientes · última ${data.autoLeads.ultimaAsignacion}`} onClick={() => handleOpenApp('autoleads')} />
      </div>

      {/* FEED EN VIVO */}
      <div style={{ maxWidth: '1400px', margin: '0 auto 12px' }}>
        <LiveLogFeed comercialesMap={comercialesMap} />
      </div>

      <InformeCRMButton data={data} />
    </div>
  );
}

// ==================== MÓVIL ====================

const MobileKPI = ({ label, value, sub, color }) => (
  <div style={{ background: PAL.card, borderRadius: '12px', padding: '12px 14px', border: `1px solid ${PAL.border}`, position: 'relative', overflow: 'hidden' }}>
    <div style={{ position: 'absolute', top: 0, left: 0, width: '3px', height: '100%', background: color }} />
    <div style={{ fontSize: '10px', color: PAL.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '4px' }}>{label}</div>
    <div style={{ fontSize: '22px', fontWeight: 800, color: PAL.text, lineHeight: 1, marginBottom: '2px' }}>{value}</div>
    {sub && <div style={{ fontSize: '11px', color: PAL.textMuted }}>{sub}</div>}
  </div>
);

const MobileDashboard = ({ data, isLoading, error, onOpenApp, channelData, originData, vnvoData, centrosData, comercialesData, totalMes, fromDate, toDate, setFromDate, setToDate, handleMesEnCurso, isMesEnCurso, expandedDetail, toggleDetail, setExpandedDetail, detailLeads, detailLoading, comercialesMap }) => (
  <div className="fg-fade" style={{ padding: '14px 12px 24px', background: PAL.bg, minHeight: '100%', boxSizing: 'border-box' }}>
    <style>{`
      @keyframes dimoSpin { to { transform: rotate(360deg); } }
      @keyframes dimoPulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(1.4); } }
      @keyframes dimoDetailExpand { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes dimoLogIn { from { opacity: 0; transform: translateX(-12px); background: #d3ecdd; } to { opacity: 1; transform: translateX(0); background: #f3faf6; } }
    `}</style>

    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', background: 'white', borderRadius: '12px', padding: '10px 12px', border: `1px solid ${PAL.border}` }}>
      <div style={{ fontSize: '15px', fontWeight: 800, color: PAL.text }}>Resumen</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: PAL.textMuted }}>
        {isLoading ? <Spinner /> : <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: error ? PAL.danger : PAL.success }} />}
        <span>{isLoading ? '…' : data.lastUpdate || '-'}</span>
      </div>
    </div>

    {/* Selector de fechas móvil */}
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '12px', background: 'white', borderRadius: '12px', padding: '8px 10px', border: `1px solid ${PAL.border}`, fontSize: '12px' }}>
      <input type="date" value={fromDate} max={toDate} onChange={(e) => setFromDate(e.target.value)} style={{ flex: 1, border: 'none', background: 'transparent', fontSize: '12px', color: PAL.text, fontFamily: 'inherit', padding: '2px', outline: 'none' }} />
      <span style={{ color: PAL.textSubtle }}>→</span>
      <input type="date" value={toDate} min={fromDate} max={todayISO()} onChange={(e) => setToDate(e.target.value)} style={{ flex: 1, border: 'none', background: 'transparent', fontSize: '12px', color: PAL.text, fontFamily: 'inherit', padding: '2px', outline: 'none' }} />
      <button onClick={handleMesEnCurso} style={{ border: `1px solid ${isMesEnCurso ? PAL.primary : PAL.border}`, background: isMesEnCurso ? PAL.primary : 'white', color: isMesEnCurso ? 'white' : PAL.primary, borderRadius: '8px', padding: '4px 8px', fontSize: '10px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>Mes</button>
    </div>

    {/* SALUD — primera fila también en móvil */}
    <div style={{ marginBottom: '12px' }}>
      <PanelSalud compacto />
    </div>

    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
      <MobileKPI label="Total CRM"  value={fmt(data.crmAdmin.leads)}               sub={`${fmt(data.crmAdmin.vn)} VN · ${fmt(data.crmAdmin.vo)} VO · ${fmt(data.crmAdmin.b2b || 0)} B2B`} color={PAL.primary} />
      <MobileKPI label="AutoLeads mes" value={fmt(data.autoLeads.mes)}               sub={`${fmt(data.autoLeads.hoy)} hoy`} color={PAL.accent} />
      <MobileKPI label="Conversión" value={`${data.crmComerciales.tasaConversion}%`} sub={`${fmt(data.crmAdmin.vendidos)} vendidos`} color={PAL.success} />
      <MobileKPI label="Comerciales" value={fmt(data.crmComerciales.activos)}        sub="activos" color={PAL.warning} />
    </div>

    <ChartCard title={isMesEnCurso ? 'Leads por canal (mes)' : 'Leads por canal (periodo)'} icon="📊" action={expandedDetail === 'canal' ? 'Cerrar' : 'Detalle'} onClick={() => toggleDetail('canal')} active={expandedDetail === 'canal'}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
        <DonutChart data={channelData} size={140} thickness={20} centerLabel="Total" centerValue={fmt(totalMes)} />
      </div>
      <Legend data={channelData} />
    </ChartCard>
    {expandedDetail === 'canal' && (
      <>
        <div style={{ height: '8px' }} />
        <DetailPanel title="Leads por canal — desglose" icon="📊" accent={PAL.primary} onClose={() => setExpandedDetail(null)}>
          <CanalDetail leads={detailLeads} loading={detailLoading} channelData={channelData} totalMes={totalMes} />
        </DetailPanel>
      </>
    )}

    <div style={{ height: '12px' }} />

    <ChartCard title={isMesEnCurso ? 'Origen del lead (mes)' : 'Origen del lead (periodo)'} icon="🎯" action={expandedDetail === 'origen' ? 'Cerrar' : 'Detalle'} onClick={() => toggleDetail('origen')} active={expandedDetail === 'origen'}>
      {originData.length === 0 ? (
        <div style={{ padding: '20px 0', textAlign: 'center', color: PAL.textSubtle, fontSize: '12px' }}>Sin datos en el periodo</div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
            <DonutChart data={originData} size={140} thickness={20} centerLabel="Orígenes" centerValue={originData.length} />
          </div>
          <Legend data={originData.slice(0, 6)} compact />
        </>
      )}
    </ChartCard>
    {expandedDetail === 'origen' && (
      <>
        <div style={{ height: '8px' }} />
        <DetailPanel title="Origen del lead — desglose" icon="🎯" accent={PAL.accent} onClose={() => setExpandedDetail(null)}>
          <OrigenDetail leads={detailLeads} loading={detailLoading} originData={originData} />
        </DetailPanel>
      </>
    )}

    <div style={{ height: '12px' }} />

    <ChartCard title={isMesEnCurso ? 'Tipo de venta (mes)' : 'Tipo de venta (periodo)'} icon="🚗" action={expandedDetail === 'tipoventa' ? 'Cerrar' : 'Detalle'} onClick={() => toggleDetail('tipoventa')} active={expandedDetail === 'tipoventa'}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
        <DonutChart data={vnvoData} size={140} thickness={20} centerLabel="Total" centerValue={fmt((data.autoLeads.leadsVN || 0) + (data.autoLeads.leadsVO || 0) + (data.autoLeads.leadsB2B || 0))} />
      </div>
      <Legend data={vnvoData} />
      <div style={{ marginTop: '10px', paddingTop: '8px', borderTop: `1px dashed ${PAL.border}`, fontSize: '11px', color: PAL.textMuted, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Pendientes</span>
          <span style={{ color: data.autoLeads.pendientes > 0 ? PAL.warning : PAL.success, fontWeight: 700 }}>{fmt(data.autoLeads.pendientes)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Por comercial</span>
          <span style={{ color: PAL.text, fontWeight: 700 }}>{fmt(data.autoLeads.leadsPorComercial)}</span>
        </div>
      </div>
      <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: `1px dashed ${PAL.border}`, fontSize: '10px', color: PAL.textSubtle }}>
        <div style={{ fontWeight: 700, color: PAL.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '4px' }}>CRM histórico</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '6px' }}>
          <span><span style={{ color: '#005187', fontWeight: 700 }}>VN</span> {fmt(data.crmAdmin.vn)}</span>
          <span><span style={{ color: '#e08c1e', fontWeight: 700 }}>VO</span> {fmt(data.crmAdmin.vo)}</span>
          <span><span style={{ color: '#7a5bd0', fontWeight: 700 }}>B2B</span> {fmt(data.crmAdmin.b2b || 0)}</span>
        </div>
      </div>
    </ChartCard>
    {expandedDetail === 'tipoventa' && (
      <>
        <div style={{ height: '8px' }} />
        <DetailPanel title="Tipo de venta — desglose" icon="🚗" accent="#e08c1e" onClose={() => setExpandedDetail(null)}>
          <TipoVentaDetail leads={detailLeads} loading={detailLoading} data={data} />
        </DetailPanel>
      </>
    )}

    <div style={{ height: '12px' }} />

    <ChartCard title="Leads por centro (periodo)" icon="🏢" action={expandedDetail === 'centros' ? 'Cerrar' : 'Detalle'} onClick={() => toggleDetail('centros')} active={expandedDetail === 'centros'}>
      <HorizontalBars data={centrosData} height={18} />
    </ChartCard>
    {expandedDetail === 'centros' && (
      <>
        <div style={{ height: '8px' }} />
        <DetailPanel title="Leads por centro — desglose" icon="🏢" accent="#2e9e5b" onClose={() => setExpandedDetail(null)}>
          <CentrosDetail leads={detailLeads} loading={detailLoading} centrosData={centrosData} comercialesMap={comercialesMap} />
        </DetailPanel>
      </>
    )}

    <div style={{ height: '12px' }} />

    <ChartCard title="Asignaciones por comercial" icon="👥" action={expandedDetail === 'comerciales' ? 'Cerrar' : 'Detalle'} onClick={() => toggleDetail('comerciales')} active={expandedDetail === 'comerciales'}>
      {comercialesData.length === 0 ? (
        <div style={{ padding: '24px 0', textAlign: 'center', color: PAL.textSubtle, fontSize: '12px' }}>Sin asignaciones en el periodo</div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
            <DonutChart data={comercialesData} size={150} thickness={22} centerLabel="Total" centerValue={fmt(comercialesData.reduce((s, d) => s + d.value, 0))} />
          </div>
          <Legend data={comercialesData} compact />
        </>
      )}
    </ChartCard>
    {expandedDetail === 'comerciales' && (
      <>
        <div style={{ height: '8px' }} />
        <DetailPanel title="Asignaciones por comercial — desglose" icon="👥" accent="#7a5bd0" onClose={() => setExpandedDetail(null)}>
          <ComercialesDetail leads={detailLeads} loading={detailLoading} comercialesData={comercialesData} comercialesMap={comercialesMap} />
        </DetailPanel>
      </>
    )}

    <div style={{ height: '12px' }} />

    <AppCard title="Email Leads"     color="#005187" hoy={data.emailLeads.hoy}    semana={data.emailLeads.semana}    mes={data.emailLeads.mes}    onClick={() => onOpenApp('email-leads')} />
    <div style={{ height: '8px' }} />
    <AppCard title="Customer Leads"  color="#1a9cd4" hoy={data.customerLeads.hoy} semana={data.customerLeads.semana} mes={data.customerLeads.mes} onClick={() => onOpenApp('customer-leads')} />
    <div style={{ height: '8px' }} />
    <AppCard title="Meta Leads"      color="#7cc4e8" hoy={data.metaLeads.hoy}     semana={data.metaLeads.semana}     mes={data.metaLeads.mes}     onClick={() => onOpenApp('fb-meta-leads')} />
    <div style={{ height: '8px' }} />
    <AppCard title="AutoLeads"       color="#2e9e5b" hoy={data.autoLeads.hoy}     semana={data.autoLeads.semana}     mes={data.autoLeads.mes}     sub={`${fmt(data.autoLeads.pendientes)} pendientes`} onClick={() => onOpenApp('autoleads')} />

    <div style={{ height: '12px' }} />

    <LiveLogFeed comercialesMap={comercialesMap} />
  </div>
);

export default Dashboard;
